"use client";

import {
  Avatar,
  Button,
  Card,
  ChevronDownIcon,
  cn,
  Combobox,
  CompanyAvatarFallbackIcon,
  DrawerBottom,
  DropdownMenu,
  FeatureIcon,
  Separator,
  SettingsIcon,
} from "@pharaday/lighthouse";
import Link from "next/link";
import { Fragment, useState } from "react";

import UserMenuItem, { type UserMenuItemProps } from "./UserMenuItem";

export interface UserMenuUserInfo {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

interface UserNamespace {
  id: string;
  name: string;
  spaceSettings?: {
    urlRoot: string;
  };
}

export interface UserMenuProps {
  items: UserMenuItemProps[];
  userInfo?: UserMenuUserInfo;
  companyProfile?: {
    id: string;
    name: string;
    logoUrl: string;
  };
  namespace?: UserNamespace & { avatarUrl?: string };
  userNamespaces?: UserNamespace[];
  onNamespaceChange?: (namespace: UserNamespace) => void;
  compactMode?: boolean;
}

const UserMenu = ({
  userInfo,
  companyProfile,
  namespace,
  items,
  userNamespaces,
  onNamespaceChange,
  compactMode,
}: UserMenuProps) => {
  const [open, setOpen] = useState(false);

  const toggleDropdownMenu = () => setOpen(prev => !prev);

  const formatSubText = () => {
    if (!companyProfile) {
      return;
    }

    return companyProfile.name + (namespace ? ` - ${namespace.name}` : "");
  };

  if (!userInfo || !companyProfile) {
    return null;
  }

  return (
    <>
      <div className={cn(compactMode ? "hidden" : "hidden mobile-screen:block")}>
        <DropdownMenu.Root open={open} onOpenChange={toggleDropdownMenu}>
          <DropdownMenu.Trigger asChild>
            <div className="w-full cursor-pointer">
              <Avatar.Root mainText={userInfo.name} subText={formatSubText()} size="sm">
                <Avatar.Image src={userInfo.avatarUrl} alt="user-avatar" />
                <Avatar.Fallback text={userInfo.name} />
                <Avatar.Indicator src={namespace?.avatarUrl} />
              </Avatar.Root>
            </div>
          </DropdownMenu.Trigger>

          <DropdownMenu.Content className="w-[296px] gap-2 bg-neutral-98" side="bottom" align="end" sideOffset={8}>
            <DropdownMenu.Group className="overflow-hidden rounded-lg bg-white p-4">
              <Avatar.Root className="mx-auto" size="lg">
                <Avatar.Image src={userInfo.avatarUrl} alt="user-avatar" />
                <Avatar.Fallback text={userInfo.name} />
              </Avatar.Root>
              <div className="mt-3 text-center">
                <p className="truncate text-base leading-6 font-bold text-neutral-10">{userInfo.name}</p>
                <p className="truncate text-sm leading-5 font-semibold text-neutral-50">{userInfo.email}</p>
              </div>
            </DropdownMenu.Group>

            {namespace && (
              <DropdownMenu.Group className="overflow-hidden rounded-lg bg-white p-4">
                <p className="h-3 text-[11px] leading-3 font-bold text-neutral-50 uppercase">YOUR SPACES</p>
                <Card.Root className="mt-3 px-2 py-3" variant="secondary">
                  <Combobox.Root className="flex justify-center">
                    <Combobox.Trigger>
                      <button>
                        <FeatureIcon size="sm" variant="light-circle-primary" className="mx-auto bg-primary-95">
                          <CompanyAvatarFallbackIcon className="size-4 text-primary-70" />
                        </FeatureIcon>
                        <span className="mt-2 flex items-center justify-center text-center text-sm leading-5 font-semibold text-neutral-10">
                          {namespace.name}
                          <ChevronDownIcon className="m-1 size-4" />
                        </span>
                      </button>
                    </Combobox.Trigger>
                    <Combobox.Content className="w-fit">
                      <Combobox.List>
                        {userNamespaces?.map(ns => (
                          <Combobox.Item
                            key={ns.id}
                            selected={namespace.id === ns.id}
                            onSelect={() => onNamespaceChange?.(ns)}
                            leading={
                              <FeatureIcon className="bg-primary-95" variant="light-circle-primary" size="xs">
                                <CompanyAvatarFallbackIcon className="size-4 text-primary-70" />
                              </FeatureIcon>
                            }
                            label={ns.name}
                          />
                        ))}
                      </Combobox.List>
                    </Combobox.Content>
                  </Combobox.Root>

                  {namespace.spaceSettings && (
                    <Link className="mt-3" href={`/${namespace.spaceSettings.urlRoot}`}>
                      <Button className="w-full" variant="outline" color="secondary" size="sm">
                        <SettingsIcon className="size-5 shrink-0" />
                        Space settings
                      </Button>
                    </Link>
                  )}
                </Card.Root>
              </DropdownMenu.Group>
            )}

            <DropdownMenu.Group className="overflow-hidden rounded-lg bg-white p-2">
              {items.map((itemProps, index) => (
                <UserMenuItem {...itemProps} key={`${itemProps.label}-${index}`} />
              ))}
            </DropdownMenu.Group>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>
      <div className={cn(compactMode ? "block" : "block mobile-screen:hidden")}>
        <DrawerBottom
          className="px-0"
          trigger={
            <div className="w-full cursor-pointer px-4">
              <Avatar.Root size="sm">
                <Avatar.Image src={userInfo?.avatarUrl} alt="user-avatar" />
                <Avatar.Fallback text={userInfo?.name} />
                <Avatar.Indicator src={companyProfile?.logoUrl} />
              </Avatar.Root>
            </div>
          }
        >
          <div className="flex flex-col gap-4 px-4">
            <div className="mt-3 flex w-full justify-center">
              <Avatar.Root mainText={userInfo?.name} subText={companyProfile?.name} size="sm">
                <Avatar.Image src={userInfo?.avatarUrl} alt="user-avatar" />
                <Avatar.Fallback text={userInfo?.name} />
                <Avatar.Indicator src={companyProfile?.logoUrl} />
              </Avatar.Root>
            </div>
            <Separator />
            <div className="flex flex-col">
              {items.map((itemProps, index) => (
                <Fragment key={`${itemProps.label}-${index}`}>
                  {itemProps.label &&
                    (itemProps.url ? (
                      <Link href={itemProps.url} target={itemProps.target} className="flex gap-2">
                        <button
                          className={cn(
                            "flex flex-1 cursor-pointer items-center gap-3 rounded-lg py-2.5 text-base font-semibold text-neutral-30 hover:bg-neutral-98",
                            itemProps.color === "error" && "text-error-50",
                            itemProps.className
                          )}
                          onClick={itemProps?.handler}
                        >
                          {itemProps.Icon && <itemProps.Icon className="h-4 w-4 shrink-0" />}
                          {itemProps.label}
                        </button>
                      </Link>
                    ) : (
                      <button
                        className={cn(
                          "flex w-full flex-1 cursor-pointer items-center gap-3 rounded-lg py-2.5 text-base font-semibold text-neutral-30 hover:bg-neutral-98",
                          itemProps.color === "error" && "text-error-50",
                          itemProps.className
                        )}
                        onClick={itemProps.handler}
                      >
                        {itemProps.Icon && <itemProps.Icon className="h-4 w-4 shrink-0" />}
                        {itemProps.label}
                      </button>
                    ))}
                  {itemProps.separator && <DropdownMenu.Separator />}
                </Fragment>
              ))}
            </div>
          </div>
        </DrawerBottom>
      </div>
    </>
  );
};

export default UserMenu;
